import { memo, useCallback, useMemo, type KeyboardEvent } from 'react';

import FilterScrollableList from '~/shared/components/filters/FilterScrollableList';
import SearchFilterOptionRow from '~/shared/components/filters/search/common/SearchFilterOptionRow';

import {
  toggleUnsupportedGroup,
  type SupportFilterGroup,
} from '../../lib/filters/buildCheckboxFilterOptions';
import { useFilterListKeyboardNav } from './hooks/useFilterListKeyboardNav';

interface SupportRowData {
  key: string;
  label: string;
  count: number;
  color?: string;
  indented?: boolean;
  checked: boolean;
  indeterminate?: boolean;
  unsupportedChildKeys?: string[];
}

interface SupportFilterListProps {
  groups: SupportFilterGroup[];
  pendingSupportStatuses: string[];
  onToggleStatus: (key: string) => void;
  onSetPendingSupportStatuses: (statuses: string[]) => void;
  isDisclosureOpen?: boolean;
}

function supportRowId(key: string): string {
  return `support-filter-row-${key}`;
}

function buildSupportRowData(
  groups: SupportFilterGroup[],
  pendingSupportStatuses: string[]
): SupportRowData[] {
  const rows: SupportRowData[] = [];

  groups.forEach((group) => {
    if (group.key === 'unsupported') {
      const children = group.children ?? [];
      const childKeys = children.map((child) => child.key);
      const selectedChildren = childKeys.filter((key) => pendingSupportStatuses.includes(key));
      const allSelected =
        childKeys.length > 0 && selectedChildren.length === childKeys.length;
      const someSelected = selectedChildren.length > 0 && !allSelected;

      rows.push({
        key: group.key,
        label: group.label,
        count: group.count,
        color: group.color,
        checked: allSelected,
        indeterminate: someSelected,
        unsupportedChildKeys: childKeys,
      });

      children.forEach((child) => {
        rows.push({
          key: child.key,
          label: child.label,
          count: child.count,
          indented: true,
          checked: pendingSupportStatuses.includes(child.key),
        });
      });
      return;
    }

    rows.push({
      key: group.key,
      label: group.label,
      count: group.count,
      color: group.color,
      checked: pendingSupportStatuses.includes(group.key),
    });
  });

  return rows;
}

interface SupportFilterListRowProps {
  rowKey: string;
  label: string;
  count: number;
  color?: string;
  indented?: boolean;
  checked: boolean;
  indeterminate?: boolean;
  index: number;
  isFocused: boolean;
  tabIndex: number;
  onActivateAtIndex: (index: number) => void;
  onRowKeyDown: (index: number, e: KeyboardEvent<HTMLDivElement>) => void;
  rowRef: (el: HTMLDivElement | null) => void;
}

const SupportFilterListRow = memo(function SupportFilterListRow({
  rowKey,
  label,
  count,
  color,
  indented,
  checked,
  indeterminate,
  index,
  isFocused,
  tabIndex,
  onActivateAtIndex,
  onRowKeyDown,
  rowRef,
}: SupportFilterListRowProps) {
  const handleToggle = useCallback(() => onActivateAtIndex(index), [onActivateAtIndex, index]);
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => onRowKeyDown(index, e),
    [onRowKeyDown, index]
  );

  return (
    <SearchFilterOptionRow
      ref={rowRef}
      id={supportRowId(rowKey)}
      label={label}
      count={count}
      color={color}
      indented={indented}
      isSelected={checked}
      indeterminate={indeterminate}
      isFocused={isFocused}
      tabIndex={tabIndex}
      onToggle={handleToggle}
      onKeyDown={handleKeyDown}
    />
  );
});

function renderSupportRow(
  row: SupportRowData,
  index: number,
  focusedIndex: number,
  onActivateAtIndex: (index: number) => void,
  handleRowKeyDown: (index: number, e: KeyboardEvent<HTMLDivElement>) => void,
  getRowRef: (index: number) => (el: HTMLDivElement | null) => void
) {
  return (
    <SupportFilterListRow
      key={row.key}
      rowKey={row.key}
      label={row.label}
      count={row.count}
      color={row.color}
      indented={row.indented}
      checked={row.checked}
      indeterminate={row.indeterminate}
      index={index}
      isFocused={focusedIndex === index}
      tabIndex={focusedIndex === index ? 0 : -1}
      onActivateAtIndex={onActivateAtIndex}
      onRowKeyDown={handleRowKeyDown}
      rowRef={getRowRef(index)}
    />
  );
}

export default function SupportFilterList({
  groups,
  pendingSupportStatuses,
  onToggleStatus,
  onSetPendingSupportStatuses,
  isDisclosureOpen = false,
}: SupportFilterListProps) {
  const rows = useMemo(
    () => buildSupportRowData(groups, pendingSupportStatuses),
    [groups, pendingSupportStatuses]
  );

  const activateAtIndex = useCallback(
    (index: number) => {
      const row = rows[index];
      if (!row) return;

      if (row.key === 'unsupported' && row.unsupportedChildKeys) {
        onSetPendingSupportStatuses(
          toggleUnsupportedGroup(row.unsupportedChildKeys, pendingSupportStatuses)
        );
        return;
      }
      onToggleStatus(row.key);
    },
    [rows, onToggleStatus, onSetPendingSupportStatuses, pendingSupportStatuses]
  );

  const onActivate = useCallback(
    (index: number) => activateAtIndex(index),
    [activateAtIndex]
  );

  const { focusedIndex, getRowRef, handleRowKeyDown } = useFilterListKeyboardNav({
    itemCount: rows.length,
    onActivate,
    isDisclosureOpen,
    focusOnOpen: 'firstRow',
  });

  const unsupportedParentIndex = rows.findIndex((row) => row.key === 'unsupported');
  let unsupportedChildEnd = unsupportedParentIndex + 1;
  if (unsupportedParentIndex >= 0) {
    while (unsupportedChildEnd < rows.length && rows[unsupportedChildEnd].indented) {
      unsupportedChildEnd += 1;
    }
  }

  return (
    <FilterScrollableList>
      {rows.map((row, index) => {
        if (unsupportedParentIndex >= 0 && row.indented && index > unsupportedParentIndex) {
          return null;
        }

        if (index === unsupportedParentIndex) {
          const childRows = rows.slice(unsupportedParentIndex + 1, unsupportedChildEnd);

          return (
            <div key={row.key}>
              {renderSupportRow(
                row,
                index,
                focusedIndex,
                activateAtIndex,
                handleRowKeyDown,
                getRowRef
              )}
              {childRows.length > 0 ? (
                <div role="group" aria-labelledby={supportRowId('unsupported')}>
                  {childRows.map((childRow, childOffset) =>
                    renderSupportRow(
                      childRow,
                      unsupportedParentIndex + 1 + childOffset,
                      focusedIndex,
                      activateAtIndex,
                      handleRowKeyDown,
                      getRowRef
                    )
                  )}
                </div>
              ) : null}
            </div>
          );
        }

        return renderSupportRow(
          row,
          index,
          focusedIndex,
          activateAtIndex,
          handleRowKeyDown,
          getRowRef
        );
      })}
    </FilterScrollableList>
  );
}
